import {isNullOrUndefined} from 'utils/general';

/**
 * A scheduled maintenance window that drives {@link MaintenanceNotice}.
 */
export interface MaintenanceWindow {
  /**
   * Stable identifier for this window. Also used as the dismissal key, so a
   * newly scheduled window re-shows even if a previous one was dismissed.
   */
  readonly id: string;
  /** Start of the expected disruption. ISO 8601 with an explicit UTC offset. */
  readonly start: string;
  /** End of the expected disruption. ISO 8601 with an explicit UTC offset. */
  readonly end: string;
  /**
   * When the pre-disruption warning begins appearing. ISO 8601 with an
   * explicit UTC offset.
   */
  readonly showFrom: string;
  /**
   * Hostnames this notice applies to. When set, the notice shows only on
   * these hosts; when omitted, it shows on every host. A host-restricted
   * notice never shows when the current hostname is unknown.
   */
  readonly hosts?: readonly string[];
}

/**
 * The active maintenance window, or `null` when none is scheduled (notice
 * disabled). Set it to a concrete {@link MaintenanceWindow} to schedule one.
 */
// No maintenance scheduled. The Netlify → Firebase Hosting cutover
// (id: hosting-cutover-2026-07-29) completed; its notice is retired. To
// schedule a future window, set this to a concrete MaintenanceWindow (see the
// interface's `@example`).
export const MAINTENANCE_NOTICE: MaintenanceWindow | null = null;

/** Which part of its lifecycle a resolved maintenance window is in. */
export type MaintenancePhase = 'upcoming' | 'in-progress';

/** A maintenance window resolved against a specific `now`, ready to render. */
export interface ResolvedMaintenanceNotice {
  /** The window's {@link MaintenanceWindow.id}. */
  readonly id: string;
  /** `upcoming` before the disruption starts, `in-progress` during it. */
  readonly phase: MaintenancePhase;
  /** Parsed {@link MaintenanceWindow.start}, epoch ms. */
  readonly startMs: number;
  /** Parsed {@link MaintenanceWindow.end}, epoch ms. */
  readonly endMs: number;
}

/**
 * Resolves whether the maintenance notice should show at `now`, and in which
 * phase.
 *
 * The notice is visible over the half-open interval `[showFrom, end)` — it
 * appears at `showFrom` (inclusive) and stops at `end` (exclusive). Within
 * that, the phase is `in-progress` over `[start, end)` (start inclusive) and
 * `upcoming` before `start`.
 *
 * @param params.now - Current time, epoch ms. Callers should pass a
 *   server-synced clock (see `useServerTime`) so a skewed client clock can't
 *   show or hide the notice at the wrong moment.
 * @param params.hostname - The current host (e.g. `window.location.hostname`).
 *   Only consulted when the window sets {@link MaintenanceWindow.hosts}; when
 *   omitted against a host-restricted window, nothing shows (fail closed).
 * @param params.notice - Window to resolve; defaults to the configured
 *   {@link MAINTENANCE_NOTICE}. Pass explicitly in tests.
 * @return The notice to render, or `null` when nothing should show — i.e. the
 *   config is `null`, `hostname` is out of the window's host scope, any
 *   instant fails to parse, or `now` falls outside `[showFrom, end)`.
 * @throws Never — malformed instants resolve to `null` rather than throwing.
 */
export function resolveMaintenanceNotice({
  now,
  hostname,
  notice = MAINTENANCE_NOTICE,
}: {
  now: number;
  hostname?: string;
  notice?: MaintenanceWindow | null;
}): ResolvedMaintenanceNotice | null {
  if (isNullOrUndefined(notice)) {
    return null;
  }
  if (
    !isNullOrUndefined(notice.hosts) &&
    (isNullOrUndefined(hostname) || !notice.hosts.includes(hostname))
  ) {
    return null;
  }
  const startMs = Date.parse(notice.start);
  const endMs = Date.parse(notice.end);
  const showFromMs = Date.parse(notice.showFrom);
  if (
    Number.isNaN(startMs) ||
    Number.isNaN(endMs) ||
    Number.isNaN(showFromMs)
  ) {
    return null;
  }
  if (now < showFromMs || now >= endMs) {
    return null;
  }
  return {
    id: notice.id,
    phase: now >= startMs ? 'in-progress' : 'upcoming',
    startMs,
    endMs,
  };
}
